import { useState } from 'react';
import axios from 'axios';
import { client as api } from '../services/api';
import { Order } from '../types/types';
import { useSchedule } from '../context/ScheduleContext';
import { digitsOnly } from '../utils/documento';

export type BuscarPedidoPayload = {
  idGar?: string;
  pedidoEcommerce?: string;
  order_id?: string;
  document: string;
};

function messageFromOrderFetchError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; mensagem?: string } | undefined;
    return (
      data?.message ??
      data?.mensagem ??
      error.message ??
      'Não foi possível buscar os pedidos. Verifique sua conexão e tente novamente.'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Não foi possível buscar os pedidos. Verifique sua conexão e tente novamente.';
}

export const useFetchOrder = () => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [statuses, setStatuses] = useState<Record<string, boolean>>({});
  const { setData } = useSchedule();

  const executeFetch = async (fetchFn: () => Promise<any>) => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetchFn();
      const orderData = Array.isArray(response.data) ? response.data : [response.data];
      setOrder(orderData);
      const first = orderData[0] as Order | undefined;
      if (first?.pedidoEcommerce) {
        setData((prevState) => ({
          ...prevState,
          pedidoEcommerce: String(first.pedidoEcommerce),
        }));
      }
      await checkScheduledOrder(orderData);
    } catch (err) {
      console.error('Erro ao buscar pedidos:', err);
      setError(messageFromOrderFetchError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderByBuscarPedido = async (payload: BuscarPedidoPayload) => {
    const document = digitsOnly(payload.document);
    const idGar = payload.idGar ? digitsOnly(payload.idGar) : undefined;
    const pedidoEcommerce =
      String(payload.pedidoEcommerce ?? payload.order_id ?? '').trim() || undefined;

    if (!document) {
      setError('CPF ou CNPJ do titular é obrigatório.');
      setLoading(false);
      return;
    }

    if (!idGar && !pedidoEcommerce) {
      setError('Número do pedido é obrigatório.');
      setLoading(false);
      return;
    }

    const body: Record<string, string> = { document };

    if (idGar) body.idGar = idGar;

    if (pedidoEcommerce) {
      body.pedidoEcommerce = pedidoEcommerce;
      body.order_id = pedidoEcommerce;
    }

    await executeFetch(() => api.post('/agendamento/buscar-pedido', body));
  };

  const fetchOrderByGarDocument = async (garId: string, document: string) => {
    await fetchOrderByBuscarPedido({ idGar: garId, document });
  };

  const checkScheduledOrder = async (orders: Order[]) => {
    const statusMap: Record<string, boolean> = {};
    const flatOrders = orders.flat();

    try {
      await Promise.all(
        flatOrders.map(async (orderItem) => {
          const response = await api.get(`/agendamento/status/${orderItem.id}`);
          statusMap[orderItem.id] = response.data.status?.descricao === 'Agendado';
        })
      );
      setStatuses(statusMap);
    } catch (err) {
      console.error('Erro ao buscar status dos pedidos:', err);
    }
  };

  return {
    order,
    loading,
    error,
    fetchOrderByBuscarPedido,
    fetchOrderByGarDocument,
    statuses,
    setStatuses,
  };
};
